import { type SearchableJobStore } from '@/features/jobs/list/SearchableJobStore'
import { action, computed, observable, runInAction } from 'mobx'
import { catchError, EMPTY, filter, startWith, switchMap } from 'rxjs'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import { type SelectFilter } from '@/infra/SelectFilter'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { Id } from '@/infra/id'
import { JobSearchQueryStringBuilder } from '@/features/jobs/list/JobSearchQueryStringBuilder'
import { type JobStatus } from '@/features/jobs/job-status'
import { type JobStatusFilterOption } from '@/features/jobs/list/job-status-filter-option'
import { type FirmDashboardContractViewModel } from '@/features/jobs/dashboard/FirmDashboardContractViewModel'
import { toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'

/**
 * View model for managing the quick select filters.
 */
export class FirmDashboardQuickSelectFilterViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Total count for under construction jobs.
   */
  @observable
  accessor underConstructionJobsCount = 0

  /**
   * Total count for in progress jobs.
   */
  @observable
  accessor inProgressJobsCount = 0

  /**
   * Total count for completed jobs.
   */
  @observable
  accessor completedJobsCount = 0

  /**
   * Total count for info gathering jobs.
   */
  @observable
  accessor infoGatheringJobsCount = 0

  constructor(
    private readonly _searchableJobStore: SearchableJobStore,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _getWorkspace: () => Workspace,
    private readonly _contractVm: FirmDashboardContractViewModel,
    private readonly _getStatusFilter: () => SelectFilter<
      string,
      JobStatusFilterOption
    > | null,
  ) {
    this._lifecycle.manage(
      toStream(() => this._getWorkspace(), true).pipe(
        filter((workspace) => Id.some(workspace.id)),
        switchMap((workspace) =>
          this._searchableJobStore.jobEvents$.pipe(
            filter(
              (event) =>
                event.type === 'JobUpdated' &&
                event.job.workspace.id === workspace.id,
            ),
            startWith(null),
            switchMap(() =>
              withAbortSignal((signal) =>
                this._fetchJobsStatusCount(workspace, signal),
              ).pipe(catchError(() => EMPTY)),
            ),
          ),
        ),
      ),
    )
  }

  /**
   * Current workspace to filter by.
   */
  @computed
  get workspace() {
    return this._getWorkspace()
  }

  /**
   * Whether the current workspace is B2B2C.
   */
  @computed
  get isB2B2CWorkspace(): boolean {
    return this.workspace.classification === 'B2B2C'
  }

  /**
   * Whether the job list has a status filter for the options to drive.
   */
  @computed
  get hasStatusFilter(): boolean {
    return this._getStatusFilter() !== null
  }

  /**
   * The number of jobs the quick select option matches.
   */
  getTotalJobsFilterCount(option: JobStatusFilterOption): number {
    switch (option) {
      case 'Onboarding':
        return this.underConstructionJobsCount
      case 'InProgress':
        return this.inProgressJobsCount
      case 'Closed':
        return this.completedJobsCount
      case 'InfoGathering':
        return this.infoGatheringJobsCount
      default:
        return 0
    }
  }

  /**
   * Whether the job list is filtered by the quick select option.
   */
  isOptionActive(option: JobStatusFilterOption) {
    return this._getStatusFilter()?.value[0] === option
  }

  /**
   * Filters the job list by the quick select option, or clears the filter
   * when it already is.
   */
  @action.bound
  toggleOption(option: JobStatusFilterOption) {
    const statusFilter = this._getStatusFilter()
    if (!statusFilter) {
      return
    }
    if (statusFilter.isSelected(option)) {
      statusFilter.clearValue()
    } else if (option === 'InProgress') {
      statusFilter.change(['InProgress', 'OnHold', 'Idle'])
    } else {
      statusFilter.change([option])
    }
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetch the total count for jobs with status:
   * `UnderConstruction`, `Claimed`, `Closed` and `InfoGathering`
   */
  private async _fetchJobsStatusCount(
    workspace: Workspace,
    signal: AbortSignal,
  ) {
    const getCountForStatuses = (statuses: JobStatus[]) =>
      this._searchableJobStore.fetchTotal(
        {
          scope: 'All',
          filter: new JobSearchQueryStringBuilder()
            .withWorkspaceIds([workspace.id])
            .withStatuses(statuses)
            .withinDateCreatedRange({
              from: this._contractVm.contractStartDate,
              to: this._contractVm.contractEndDate,
            })
            .build(),
          limit: 0,
        },
        signal,
      )

    const withRetry = this._createAlertRetryWrapper(
      'Loading job counts failed',
      signal,
    )
    const counts = await withRetry(() =>
      Promise.all([
        getCountForStatuses(['UnderConstruction']),
        getCountForStatuses(['Claimed', 'OnHold', 'Idle']),
        getCountForStatuses(['Closed']),
        getCountForStatuses(['InfoGathering']),
      ]),
    )
    signal.throwIfAborted()

    const [
      underConstructionCount,
      inProgressCount,
      completedCount,
      infoGatheringCount,
    ] = counts

    runInAction(() => {
      this.underConstructionJobsCount = underConstructionCount
      this.inProgressJobsCount = inProgressCount
      this.completedJobsCount = completedCount
      this.infoGatheringJobsCount = infoGatheringCount
    })
  }
}
